import axios from "axios";
// import { getAcceptedByMe } from "../../../hr_portal_backend/src/controllers/offerController";

/* ================= BASE CONFIG ================= */
// Change to localhost for local run:
const API_BASE_URL = `${process.env.REACT_APP_API_BASE || window.location.origin}/api`;
// Keep headcount on the same API host/path as the rest of the app to avoid
// cross-origin preflight issues in production.
const HEADCOUNT_API_BASE_URL = API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
});

const headcountApi = axios.create({
  baseURL: HEADCOUNT_API_BASE_URL,
});

const ANALYTICS_SESSION_KEY = "hrportal_analytics_session_id";

function analyticsSessionId() {
  let id = sessionStorage.getItem(ANALYTICS_SESSION_KEY);
  if (!id) {
    id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem(ANALYTICS_SESSION_KEY, id);
  }
  return id;
}

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const attachJwtInterceptor = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      // Only default the portal token when the caller did NOT explicitly set
      // an Authorization header (e.g. Sudo-admin endpoints pass adminToken).
      // Without this guard, a lingering portal token would overwrite the Sudo
      // admin token and break admin users/roles/access API calls with 403s.
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // The server middleware copies these onto the api_call row it writes,
      // which is what attributes a request to the screen it was made from.
      config.headers["X-Analytics-Session-Id"] = analyticsSessionId();
      const pageKey = sessionStorage.getItem("hrportal_analytics_page_key");
      const tabName = sessionStorage.getItem("hrportal_analytics_tab_name");
      if (pageKey) config.headers["X-Analytics-Page-Key"] = pageKey;
      if (tabName) config.headers["X-Analytics-Tab-Name"] = tabName;
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url?.includes("/auth/refresh-token") &&
        !originalRequest.url?.includes("/auth/verify-otp") &&
        !originalRequest.url?.includes("/auth/microsoft-login") &&
        !originalRequest.url?.includes("/auth/google-login") &&
        !originalRequest.url?.includes("/admin/login")
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem("refreshToken");

        if (!refreshToken) {
          isRefreshing = false;
          localStorage.clear();
          window.location.href = "/login";
          return Promise.reject(error);
        }

        try {
          const response = await axios.post(
            `${instance.defaults.baseURL}/auth/refresh-token`,
            { refreshToken }
          );

          const { token, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem("token", token);
          if (newRefreshToken) localStorage.setItem("refreshToken", newRefreshToken);

          processQueue(null, token);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.clear();
          window.location.href = "/login";
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

// Response interceptors run in reverse registration order. Registering
// feature-use tracking first therefore makes it observe the FINAL outcome.
// A request that 401s, refreshes its token and then succeeds must be recorded
// once as a success — not twice, with the first copy falsely marked failed.
attachFeatureUseInterceptor(api);
attachFeatureUseInterceptor(headcountApi);
attachJwtInterceptor(api);
attachJwtInterceptor(headcountApi);

export default api;

/* ================= FEATURE-USE TRACKING ================= */
// Page views alone cannot distinguish "opened the Employees screen" from
// "maintained employee records", so usage reporting was flat. Every
// state-changing request is therefore mirrored as a feature_use event, which
// makes adoption reporting work without touching a single page component.
//
// The event is posted through `api` rather than a private axios instance, so it
// carries the portal JWT and survives an access-token refresh. A private
// instance posts without an Authorization header, `protect` on
// POST /analytics/events rejects it with a 401, and the .catch() below swallows
// that silently - which is precisely how a feature_use count can sit at zero
// while the code looks correct.
// Recursion is not a concern: isTrackableMutation rejects every /analytics url,
// so an analytics write never re-enters the interceptor that produced it.
const UNTRACKED_MUTATION_PREFIXES = ["/analytics", "/auth", "/admin/login"];
const MUTATION_VERBS = { post: "create", put: "update", patch: "update", delete: "delete" };

function isTrackableMutation(config) {
  const method = (config?.method || "get").toLowerCase();
  if (!MUTATION_VERBS[method]) return false;
  const url = config.url || "";
  if (UNTRACKED_MUTATION_PREFIXES.some((prefix) => url.startsWith(prefix))) return false;
  // Only attribute an action to a page when that page is actually being tracked.
  return Boolean(sessionStorage.getItem("hrportal_analytics_page_key"));
}

function mutationActionName(config) {
  const method = (config?.method || "get").toLowerCase();
  const segments = (config?.url || "").split("?")[0].split("/").filter(Boolean);
  // The first segment names the resource; the rest is an id or sub-action.
  const resource = segments[0]?.replace(/[^a-z0-9-]/gi, "") || "resource";
  // The trailing segment is frequently the record id. Folding it into the
  // action name would split one action into one row per record touched, so only
  // a genuine sub-action is kept.
  const tail = segments.length > 2 ? segments[segments.length - 1] : null;
  const subAction = tail && !/^\d+$/.test(tail) ? tail : null;
  return [MUTATION_VERBS[method], resource, subAction].filter(Boolean).join(" ");
}

function postFeatureUse(config, { success, status, durationMs }) {
  if (!isTrackableMutation(config) || !localStorage.getItem("token")) return;
  const payload = {
    eventId: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    occurredAt: new Date().toISOString(),
    schemaVersion: 1,
    sessionId: analyticsSessionId(),
    source: "hr_portal_web",
    eventType: "feature_use",
    pageKey: sessionStorage.getItem("hrportal_analytics_page_key"),
    tabName: sessionStorage.getItem("hrportal_analytics_tab_name"),
    actionName: mutationActionName(config),
    entityType: (config.url || "").split("?")[0].split("/").filter(Boolean)[0] || null,
    success,
    durationMs,
    metadata: { method: (config.method || "get").toUpperCase(), endpoint: config.url, status },
  };
  // Analytics must never surface as an error in the portal's own workflow.
  api.post("/analytics/events", payload).catch(() => {});
}

function attachFeatureUseInterceptor(instance) {
  instance.interceptors.request.use((config) => {
    config.__analyticsStartedAt = Date.now();
    return config;
  });
  instance.interceptors.response.use(
    (response) => {
      postFeatureUse(response.config, {
        success: response.status < 400,
        status: response.status,
        durationMs: Date.now() - (response.config?.__analyticsStartedAt || Date.now()),
      });
      return response;
    },
    (error) => {
      if (error.config) {
        postFeatureUse(error.config, {
          success: false,
          status: error.response?.status || 0,
          durationMs: Date.now() - (error.config.__analyticsStartedAt || Date.now()),
        });
      }
      return Promise.reject(error);
    }
  );
}

/* ================= AUTH API ================= */
export const authAPI = {
  // Google OAuth login
  googleLogin: (token) =>
    api.post("/auth/google-login", { token }),

  // Verify OTP
  verifyOTP: (userId, otp) =>
    api.post("/auth/verify-otp", { userId, otp }),

  // Microsoft OAuth login
  microsoftLogin: (token) =>
    api.post("/auth/microsoft-login", { token }),

  // Current DB-backed profile (role/name from the database, not the JWT)
  getMe: () => api.get("/auth/me"),
};

/* ================= ADMIN API ================= */
export const adminAPI = {
  login: (data) => api.post("/admin/login", data),
  addUser: (data) => api.post("/admin/add-user", data),
};

/* ================= CANDIDATES API ================= */
export const candidatesAPI = {
  getAll: (params = {}) => api.get("/candidates", { params }),
  getById: (id) => api.get(`/candidates/${id}`),
  create: (data) => api.post("/candidates", data),
  update: (id, data) => api.put(`/candidates/${id}`, data),
  delete: (id) => api.delete(`/candidates/${id}`),
  getRemarks: (candidateId) => api.get(`/candidates/${candidateId}/remarks`),
  addRemark: (candidateId, data) => api.post(`/candidates/${candidateId}/remarks`, data),
  getTimeline: (candidateId) => api.get(`/candidates/${candidateId}/timeline`),
  getCreators: (params = {}) => api.get("/candidates/creators", { params }),
};

/* ================= ANALYTICS API ================= */
// Analytics now lives under Sudo Access (/admin/analytics, Sudo JWT). Requests
// originating from a Sudo page attach the Sudo admin token (existing
// adminAuthHeaders pattern); portal pages keep the existing HR-token behavior.
// Request params, response handling and business logic are unchanged.
function sudoAnalyticsConfig(extra = {}) {
  try {
    if (
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/admin")
    ) {
      const adminToken = localStorage.getItem("adminToken");
      if (adminToken) {
        return { ...extra, headers: { ...(extra.headers || {}), Authorization: `Bearer ${adminToken}` } };
      }
    }
  } catch {
    // ignore storage/location errors — fall back to portal-token behavior
  }
  return extra;
}
export const analyticsAPI = {
  getSummary: (params = {}) => api.get("/analytics/summary", sudoAnalyticsConfig({ params })),
  getStatus: () => api.get("/analytics/status", sudoAnalyticsConfig()),
  syncPeople: () => api.post("/analytics/sync-people", null, sudoAnalyticsConfig()),
};


export const feedbackAPI = {
  create: (data) => api.post("/feedback", data),
  getByCandidate: (candidateId) => api.get(`/feedback/${candidateId}`),
  export: (candidateId) => api.get(`/feedback/${candidateId}/export`, { responseType: "blob" }),
  getRequests: (candidateId) => api.get(`/feedback/${candidateId}/requests`),
  resend: (candidateId) => api.post(`/feedback/${candidateId}/requests/resend`),
};

export const profileScreeningsAPI = {
  send: (candidateId) => api.post(`/profile-screenings/candidates/${candidateId}`),
  getByCandidate: (candidateId) => api.get(`/profile-screenings/candidates/${candidateId}`),
};

/* ================= INTERVIEWS API ================= */
export const interviewsAPI = {
  getAll: (params) => api.get("/interviews", { params }),
  getByCandidate: (candidateId) =>
    api.get(`/interviews/candidate/${candidateId}`),
  create: (data) => api.post("/interviews", data),
  cancel: (id) => api.post(`/interviews/${id}/cancel`),
  update: (id, data) =>
    api.put(`/interviews/${id}`, data),
  updateStatus: (id, data) =>
    api.put(`/interviews/${id}/status`, data),
  generateLink: (data) => api.post("/interviews/generate-link", data),
  getLiveSessions: () => api.get("/interviews/live/sessions"),
  getLiveSession: (sessionId) => api.get(`/interviews/live/${sessionId}`),
};


/* ================= OFFERS API ================= */

export const offersAPI = {
  getAll: (params) =>
    api.get("/offers", { params }),

  getMyPending: () =>
    api.get("/offers/my/pending"),

  getAcceptedByMe: () =>
    api.get("/offers/my/accepted"),

  getByCandidate: (candidateId) =>
    api.get(`/offers/candidate/${candidateId}`),

  getById: (id) =>
    api.get(`/offers/${id}`),

  create: (data) =>
    api.post("/offers", data),

  update: (id, data) =>
    api.put(`/offers/${id}`, data),

  updateRecruiter: (id, data) =>
    api.put(`/offers/${id}/recruiter`, data),

  updateStage1: (id, data) =>
    api.put(`/offers/${id}/stage1`, data),

  updateStage2: (id, data) =>
    api.put(`/offers/${id}/stage2`, data),

  updateStage3: (id, data) =>
    api.put(`/offers/${id}/stage3`, data),

  respond: (id, data) =>
    api.post(`/offers/${id}/respond`, data),

  handleAction: (id, action, remarks = "") =>
    api.post(`/offers/${id}/action`, { action, remarks }),

  approve: (id, remarks = "") =>
    api.post(`/offers/${id}/action`, { action: "approve", remarks }),

  reject: (id, remarks = "") =>
    api.post(`/offers/${id}/action`, { action: "reject", remarks }),

  sendBack: (id, remarks = "") =>
    api.post(`/offers/${id}/action`, { action: "sent_back", remarks }),

  assignStage: (id, data) =>
    api.post(`/offers/${id}/assign`, data),

  workflow: (id, data) =>
    api.post(`/offers/${id}/workflow`, data),

  delete: (id) =>
    api.delete(`/offers/${id}/workflow`),

  sendOfferLetter: (data) =>
    api.post("/offers/send-offer-letter", data),

  getWorkflowEmails: () =>
    api.get("/workflow-emails"),

  saveWorkflowEmails: (data) =>
    api.post("/workflow-emails", data),

  submitForApproval: (id) =>
    api.post(`/offers/${id}/submit`),

  updateDraft: (id, data) =>
    api.put(`/offers/${id}/draft`, data),

  releaseToCandidate: (id) =>
    api.post(`/offers/${id}/release`),

  getOfferReleaseChecklist: (offerId) =>
    api.get(`/offer-release-checklist/${offerId}`),

  saveOfferReleaseChecklist: (offerId, data) =>
    api.put(`/offer-release-checklist/${offerId}`, data),

  rejectWorkflow: (id, data) =>
    api.post(`/workflow-rejection/${id}/reject`, data),

  getWorkflowHistory: (id) =>
    api.get(`/workflow-rejection/${id}/history`),

  submitOfferForApproval: (id) =>
    api.post(`/offers/${id}/submit`),

  /* PAN / AADHAAR document uploads (stored on the offer record) */
  uploadDocument: (offerId, docType, file) => {
    const formData = new FormData();
    formData.append("docType", docType);
    formData.append("file", file);
    return api.post(`/offers/${offerId}/document?docType=${docType}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  downloadDocument: (offerId, docType) =>
    api.get(`/offers/${offerId}/document/download`, {
      params: { docType },
    }),

  getAllDocuments: () =>
    api.get("/offers/documents"),
};

/* ================= OFFER MASTER API ================= */

export const offerMasterAPI = {
  getConditionalOfferMaster: () =>
    api.get("/offer-masters"),

  createConditionalOfferMaster: (data) =>
    api.post("/offer-masters", data),

  updateConditionalOfferMaster: (id, data) =>
    api.put(`/offer-masters/${id}`, data),

  deleteConditionalOfferMaster: (id) =>
    api.delete(`/offer-masters/${id}`),
};

/* ================= WORKFLOW MASTER API ================= */

export const workflowMasterAPI = {
  getOptions: (params = {}) =>
    api.get("/workflow-master/options", { params }),

  getAll: () =>
    api.get("/workflow-master"),

  create: (data) =>
    api.post("/workflow-master", data),

  update: (id, data) =>
    api.put(`/workflow-master/${id}`, data),

  delete: (id) =>
    api.delete(`/workflow-master/${id}`),
};

/* ================= STAGE 1 ASSIGNMENT API ================= */

export const stage1AssignmentAPI = {
  searchEmployees: async (search) => {
    return api.get("/stage1-assignment/employees/search", {
      params: { q: search },
    });
  },

  assign: async (candidateIds, assignedTo) => {
    return api.post("/stage1-assignment/assign", {
      candidateIds,
      assignedTo,
    });
  },

  delegate: async (assignmentId, delegatedTo, reason = "") => {
    return api.post(`/stage1-assignment/delegate/${assignmentId}`, {
      delegatedTo,
      reason,
    });
  },

  delegateMultiple: async (
    assignmentIds = [],
    delegatedTo,
    reason = "",
    candidateIds = []
  ) => {
    return api.post("/stage1-assignment/delegate-multiple", {
      assignmentIds,
      candidateIds,
      delegatedTo,
      reason,
    });
  },

  getCandidateAssignment: async (candidateId) => {
    return api.get(`/stage1-assignment/candidate/${candidateId}`);
  },

  getCandidateAssignments: async (candidateIds) => {
    return api.post("/stage1-assignment/candidates", {
      candidateIds,
    });
  },

  cancel: async (assignmentId) => {
    return api.post(`/stage1-assignment/cancel/${assignmentId}`);
  },
};

/* ================= DELEGATIONS API ================= */
export const delegationsAPI = {
  getAll: (params = {}) =>
    api.get("/delegations", { params }),

  getById: (id) =>
    api.get(`/delegations/${id}`),

  create: (data) =>
    api.post("/delegations", data),

  update: (id, data) =>
    api.put(`/delegations/${id}`, data),

  revoke: (id) =>
    api.delete(`/delegations/${id}`),

  resolve: (email, stage) =>
    api.get("/delegations/resolve", { params: { email, stage } }),

  searchUsers: (q) =>
    api.get("/delegations/users/search", { params: { q } }),

  getStages: () =>
    api.get("/delegations/stages"),

  getEligibility: () =>
    api.get("/delegations/eligibility"),
};

/* ================= OFFER LETTER PDF API ================= */
export const offerLetterAPI = {
  generatePreview: (data) => api.post("/offers/offer-letter/generate", data),
  downloadPdf: (html) =>
    api.post("/offers/offer-letter/download-pdf", { html }, { responseType: "blob" }),
};
/* ================= EMAILS API ================= */
export const emailsAPI = {
  sendInterview: (data) =>
    api.post("/emails/interview", data),
  sendHRInterviewNotification: (data) =>
    api.post("/emails/hr-interview-notification", data),
  sendOffer: (data) =>
    api.post("/emails/offer", data),
  sendWeeklyReport: (data) =>
    api.post("/emails/weekly-report/send", data),
  getLogs: () =>
    api.get("/emails/logs"),
  handleAction: (id, data) =>
    api.post(`/offers/${id}/action`, data),
};

/* ================= AI API ================= */
export const aiAPI = {
  parseResume: (formData) =>
    api.post("/ai/parse-resume", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  analyzeResume: (data) =>
    api.post("/ai/analyze", data),
  getModelStatus: () =>
    api.get("/ai/model-status"),
  getJDSuggestions: (data) =>
    api.post("/ai/jd-suggestions", data),
  getInterviewTips: (data) =>
    api.post("/ai/interview-tips", data),
  convertResumeFormat: (formData) =>
    api.post("/ai/convert-resume-format", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  generateFormattedResume: (data) =>
    api.post("/ai/generate-formatted-resume", data),
  downloadFormattedResume: (data) =>
    api.post("/ai/generate-formatted-resume", data, {
      responseType: "blob",
    }),
};

/* ================= INTERVIEWERS API ================= */
export const interviewersAPI = {
  getAll: () => api.get("/interviewers"),
  create: (data) => api.post("/interviewers", data),
  delete: (id) => api.delete(`/interviewers/${id}`),
};

/* ================= RESUMES API - ✅ FIXED & COMPLETE ================= */
export const resumesAPI = {
  // ✅ CRITICAL: Parse resume to extract candidate data (THIS WAS MISSING!)
  parseResume: (formData) =>
    api.post("/ai/parse-resume", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  // Upload resume file for a candidate
  upload: (formData) =>
    api.post("/resumes/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),

  // Get all resume updates (for Profile page)
  getAllUpdates: () => api.get("/resumes/all-updates"),

  // Get resume by candidate
  getByCandidate: (candidateId) =>
    api.get(`/resumes/candidate/${candidateId}`),

  // Get resume by ID
  getById: (id) =>
    api.get(`/resumes/${id}`),

  // Download resume
  download: (id) =>
    api.get(`/resumes/download/${id}`,
    ),
};

/* ================= ✅ PROFILE API - NEW! ================= */
export const profileAPI = {
  // ✅ STATUS ACTIVITY - THIS FIXES YOUR ERROR!
  getStatusActivity: () => api.get("/profile/status-activity"),

  // Profile info
  getProfile: () => api.get("/profile/me"),
};

/* ================= STATUS SYNC HELPER ================= */
export const notifyStatusUpdated = () => {
  localStorage.setItem("STATUS_UPDATED_AT", Date.now().toString());
};
/* ================= JOBS API ================= */
export const jobsAPI = {
  getAll: () => api.get("/jobs"),
  create: (data) => api.post("/jobs", data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
};

export const headcountAPI = {
  testZohoAuth: () => headcountApi.get("/headcount/zoho/test-auth"),
  sync: (params = {}) => headcountApi.post("/headcount/sync", null, { params }),
  getDashboard: () => headcountApi.get("/headcount/dashboard"),
  getOptions: () => headcountApi.get("/headcount/options"),
  getValidation: () => headcountApi.get("/headcount/validation"),
  getDivisionsByBU: (businessUnit) => headcountApi.get("/headcount/divisions-by-bu", { params: { businessUnit } }),
  getDepartmentsByBU: (businessUnit) => headcountApi.get("/headcount/departments-by-bu", { params: { businessUnit } }),
  getDepartmentsByDivision: (division, businessUnit) =>
    headcountApi.get("/headcount/departments-by-division", { params: { division, ...(businessUnit ? { businessUnit } : {}) } }),
  uploadExcel: (formData) =>
    headcountApi.post("/headcount/upload-excel", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  exportData: (format, params = {}) =>
    headcountApi.get(`/headcount/export/${format}`, { params, responseType: "blob" }),
};

export const employeesAPI = {
  getAll: (params) => api.get("/employees", { params }),
  getById: (id) => api.get(`/employees/${id}`),
  update: (id, data) => api.put(`/employees/${id}`, data),
  getFilterValues: () => api.get("/employees/filter-values"),
};

/* ================= ACCESS CONTROL API ================= */
// Sudo-admin auth header for admin-area endpoints (admin JWT from Sudo Login)
const adminAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
});

export const accessAPI = {
  getPages: () => api.get("/access/pages", adminAuthHeaders()),
  getUsers: () => api.get("/access/users", adminAuthHeaders()),
  getUserAccess: (userId) => api.get(`/access/users/${userId}`, adminAuthHeaders()),
  saveUserAccess: (userId, access) => api.put(`/access/users/${userId}`, { access }, adminAuthHeaders()),
  getMine: () => api.get("/access/my-access"),
};

// Role-based access control (Phase 5) — Sudo-protected role management.
export const roleAPI = {
  getRoles: () => api.get("/admin/roles", adminAuthHeaders()),
  createRole: (data) => api.post("/admin/roles", data, adminAuthHeaders()),
  updateRole: (id, data) => api.put(`/admin/roles/${id}`, data, adminAuthHeaders()),
  toggleRole: (id, isActive) => api.patch(`/admin/roles/${id}/toggle`, { isActive }, adminAuthHeaders()),
  deleteRole: (id) => api.delete(`/admin/roles/${id}`, adminAuthHeaders()),
  getRoleAccess: (id) => api.get(`/admin/roles/${id}/access`, adminAuthHeaders()),
  saveRoleAccess: (id, access) => api.put(`/admin/roles/${id}/access`, { access }, adminAuthHeaders()),
  getRoleUsers: (id) => api.get(`/admin/roles/${id}/users`, adminAuthHeaders()),
  assignUsers: (id, userIds) => api.post(`/admin/roles/${id}/assign`, { userIds }, adminAuthHeaders()),
};

export const traineesAPI = {
  getAll: (params) => api.get("/trainees", { params }),
  getById: (id) => api.get(`/trainees/${id}`),
  create: (data) => api.post("/trainees", data),
  update: (id, data) => api.put(`/trainees/${id}`, data),
  delete: (id) => api.delete(`/trainees/${id}`),
  getFilterValues: () => api.get("/trainees/filter-values"),
  exportData: (format, params = {}) =>
    api.get(`/trainees/export/${format}`, { params, responseType: "blob" }),
  getActionInfo: (token) => api.get(`/trainees/action-info?token=${token}`),
  processAction: (data) => api.post("/trainees/process-action", data),
};
export const internsAPI = {
  getAll: (params) => api.get("/interns", { params }),
  getById: (id) => api.get(`/interns/${id}`),
  create: (data) => api.post("/interns", data),
  update: (id, data) => api.put(`/interns/${id}`, data),
  delete: (id) => api.delete(`/interns/${id}`),
  getFilterValues: () => api.get("/interns/filter-values"),
  exportData: (format, params = {}) =>
    api.get(`/interns/export/${format}`, { params, responseType: "blob" }),
  getActionInfo: (token) => api.get(`/interns/action-info?token=${token}`),
  processAction: (data) => api.post("/interns/process-action", data),
};

/* ================= CUSTOMERS API ================= */
export const customersAPI = {
  getAll: () => api.get("/customers"),
  getProjects: (id) => api.get(`/customers/${id}/projects`),
  getTimeline: (id, params = {}) => api.get(`/customers/${id}/timeline`, { params }),
  create: (data) => api.post("/customers", data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  toggle: (id, isActive) => api.patch(`/customers/${id}/toggle`, { isActive }),
  delete: (id) => api.delete(`/customers/${id}`),
  exportData: (format) => api.get(`/customers/export/${format}`, { responseType: "blob" }),
};

/* ================= REVENUE API ================= */
export const revenueAPI = {
  calculate: (data) => api.post("/revenue/calculate", data),
  getSummary: (params) => api.get("/revenue/summary", { params }),
  getResourceDetails: (params) => api.get("/revenue/resource-details", { params }),
  addAdjustment: (data) => api.post("/revenue/adjust", data),
  getAdjustments: (params) => api.get("/revenue/adjustments", { params }),
  freezeMonth: (data) => api.post("/revenue/freeze", data),
  getFreezeStatus: (params) => api.get("/revenue/freeze-status", { params }),
  exportSummary: (params) => api.get("/revenue/export", { params, responseType: "blob" }),
};

/* ================= RATE MASTER API ================= */
export const rateMasterAPI = {
  getAll: (params = {}) => api.get("/rate-master", { params }),
  getMatch: (params) => api.get("/rate-master/match", { params }),
  getRoles: (params = {}) => api.get("/rate-master/roles", { params }),
  create: (data) => api.post("/rate-master", data),
  update: (id, data) => api.put(`/rate-master/${id}`, data),
  delete: (id) => api.delete(`/rate-master/${id}`),
};

/* ================= MASTERS API ================= */
export const mastersAPI = {
  getAll: (params = {}) => api.get("/masters", { params }),
  create: (data) => api.post("/masters", data),
  update: (id, data) => api.put(`/masters/${id}`, data),
  delete: (id) => api.delete(`/masters/${id}`),
};

/* ================= PROJECTS API ================= */
export const projectsAPI = {
  getAll: (params = {}) => api.get("/projects", { params }),
  getById: (id) => api.get(`/projects/${id}`),
  getCustomerId: (id) => api.get(`/projects/customer/${id}`),
  create: (data) => api.post("/projects", data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getResources: (projectId) => api.get(`/projects/${projectId}/resources`),
  addResource: (projectId, data) => api.post(`/projects/${projectId}/resources`, data),
  deleteResource: (projectId, resourceId) => api.delete(`/projects/${projectId}/resources/${resourceId}`),
  uploadAttachment: (id, formData) =>
    api.post(`/projects/${id}/attachment`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  deleteAttachment: (id) => api.delete(`/projects/${id}/attachment`),
  getResourceCostPreview: (params) => api.get("/projects/resource-cost-preview", { params }),
  getResourceAllocation: () => api.get("/projects/resource-allocation"),
  getEmployeeAllocation: (employeeId) => api.get(`/projects/resource-allocation/${employeeId}`),
  getEmployeeProjectDetails: (employeeId) => api.get(`/projects/resource-details/${encodeURIComponent(employeeId)}`),
  getClosureChecklist: (projectId) => api.get(`/projects/${projectId}/closure-checklist`),
  saveClosureChecklist: (projectId, items) => api.put(`/projects/${projectId}/closure-checklist`, { items }),
};

export const notificationsAPI = {
  getAll: () => api.get("/notifications"),
  getUnreadCount: () => api.get("/notifications/unread-count"),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch("/notifications/read-all"),
};

export const supportTicketsAPI = {
  getAll: (params = {}) => api.get("/support-tickets", { params }),
  create: (data) => api.post("/support-tickets", data),
  getById: (id) => api.get(`/support-tickets/${id}`),
  getHistory: (id) => api.get(`/support-tickets/${id}/history`),
  getDevelopers: () => api.get("/support-tickets/developers"),
  assign: (id, userId) => api.patch(`/support-tickets/${id}/assign`, { user_id: userId }),
  markStatus: (id, data) => api.patch(`/support-tickets/${id}/status`, data),
  remove: (id) => api.delete(`/support-tickets/${id}`),
};

export const reimbursementsAPI = {
  getProfile: () => api.get("/reimbursements/profile"),
  getDashboard: (params = {}) => api.get("/reimbursements/dashboard", { params }),
  exportExcel: (params = {}) => api.get("/reimbursements/export", { params, responseType: "blob" }),
  getAll: () => api.get("/reimbursements"),
  getById: (id) => api.get(`/reimbursements/${id}`),
  create: (data) => api.post("/reimbursements", data),
  update: (id, data) => api.put(`/reimbursements/${id}`, data),
  submit: (id) => api.post(`/reimbursements/${id}/submit`),
  review: (id) => api.post(`/reimbursements/${id}/review`),
  approve: (id) => api.post(`/reimbursements/${id}/approve`),
  reject: (id, reason) => api.post(`/reimbursements/${id}/reject`, { reason }),
  returnForCorrection: (id, reason) => api.post(`/reimbursements/${id}/return-for-correction`, { reason }),
  verify: (id) => api.post(`/reimbursements/${id}/verify`),
  financeReject: (id, reason) => api.post(`/reimbursements/${id}/finance-reject`, { reason }),
  financeReturn: (id, reason) => api.post(`/reimbursements/${id}/finance-return`, { reason }),
  process: (id) => api.post(`/reimbursements/${id}/process`),
  pay: (id) => api.post(`/reimbursements/${id}/pay`),
  getApprovals: () => api.get("/reimbursements/approvals"),
  getApprovedByMe: () => api.get("/reimbursements/approved-by-me"),
  getVerificationQueue: () => api.get("/reimbursements/verification"),
  getProcessingQueue: () => api.get("/reimbursements/process"),
  getAttachment: (id, lineId) => api.get(`/reimbursements/${id}/attachment/${lineId}`),
  expenseMappings: (department) => api.get("/reimbursements/masters/expense-mappings", { params: { department } }),
  projects: (search) => api.get("/reimbursements/projects", { params: { search } }),
  getProject: (id) => api.get(`/reimbursements/projects/${id}`),
  upload: (formData) => api.post("/reimbursements/attachments", formData, { headers: { "Content-Type": "multipart/form-data" } }),
};

export const salesAPI = {
  getAll: (params = {}) => api.get("/sales", { params }),
  getById: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post("/sales", data),
  update: (id, data) => api.put(`/sales/${id}`, data),
  delete: (id) => api.delete(`/sales/${id}`),
  importExcel: (formData) =>
    api.post("/sales/import-excel", formData, { headers: { "Content-Type": "multipart/form-data" } }),
};

export const trackersAPI = {
  getPayrollEntries: () => api.get("/trackers/payroll"),
  getPayrollRoster: () => api.get("/trackers/payroll/roster"),
  getPayrollEntryById: (id) => api.get(`/trackers/payroll/${id}`),
  createPayrollEntry: (data) => {
    if (data instanceof FormData) {
      return api.post("/trackers/payroll", data, { headers: { "Content-Type": "multipart/form-data" } });
    }
    return api.post("/trackers/payroll", data);
  },
  updatePayrollEntry: (id, data) => {
    if (data instanceof FormData) {
      return api.put(`/trackers/payroll/${id}`, data, { headers: { "Content-Type": "multipart/form-data" } });
    }
    return api.put(`/trackers/payroll/${id}`, data);
  },
  deletePayrollEntry: (id) => api.delete(`/trackers/payroll/${id}`),
  downloadPoAttachment: (poEntryId) => api.get(`/trackers/payroll/po-attachment/${poEntryId}`),
  deletePoAttachment: (poEntryId) => api.delete(`/trackers/payroll/po-attachment/${poEntryId}`),
  uploadPoAttachment: (formData) => api.post("/trackers/payroll/upload-attachment", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  getVariableEntries: () => api.get("/trackers/variable"),
  getVariableEntryById: (id) => api.get(`/trackers/variable/${id}`),
  createVariableEntry: (data) => api.post("/trackers/variable", data),
  updateVariableEntry: (id, data) => api.put(`/trackers/variable/${id}`, data),
  approveVariableEntries: (ids) => api.post("/trackers/variable/approve", { ids }),
  updatePaymentStatus: (id, data) => api.put(`/trackers/variable/${id}/payment-status`, data),
  checkDuplicateVariable: (params) => api.get("/trackers/variable/duplicate", { params }),
  lookupEmployeeForVariable: (employeeNumber) => api.get("/trackers/variable/lookup-employee", { params: { employeeNumber } }),
  exportTracker: (tracker, format = "csv", params = {}) => api.get(`/trackers/export/${tracker}`, { params: { format, ...params }, responseType: "blob" }),
  getAuditHistory: (module, recordId) => api.get(`/trackers/audit/${module}/${recordId}`),
  getApprovalHistory: (module, recordId) => api.get(`/trackers/approval-history/${module}/${recordId}`),
  getResourceCostEntries: () => api.get("/trackers/resource-cost"),
  getResourceCostEntryById: (id) => api.get(`/trackers/resource-cost/${id}`),
  createResourceCostEntry: (data) => api.post("/trackers/resource-cost", data),
  updateResourceCostEntry: (id, data) => api.put(`/trackers/resource-cost/${id}`, data),
  deleteResourceCostEntry: (id) => api.delete(`/trackers/resource-cost/${id}`),
};

